<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Section } from '@drama/ui'
import DashboardTrend from './DashboardTrend.vue'
import RetentionUsersModal from './RetentionUsersModal.vue'

const props = defineProps({ data: { type: Object, required: true }, kind: { type: String, required: true }, deviceTypes: { type: String, default: '' } })
const { t, locale } = useI18n()
const offsets = ['d1', 'd7', 'd30']
const milestone = ref('d1')
const target = ref(null)
watch(() => props.data, () => { target.value = null })
function inspect(date) {
  if (!props.data.daily.some(row => row.date === date)) return
  target.value = { date, offset: milestone.value.slice(1), device_types: props.deviceTypes }
}
const summary = computed(() => props.data.summary[milestone.value])
const pending = computed(() => props.data.daily.some(row => row.cohort_users > 0 && row[milestone.value].status === 'pending'))
const series = computed(() => [{
  label: t(`visualGame.dashboard.retention.${milestone.value}`), tone: props.kind === 'visits' ? 'info' : 'success',
  values: props.data.daily.map(row => row[milestone.value].rate),
}])
const has_data = computed(() => series.value[0].values.some(value => value !== null))
const eligible_range = computed(() => {
  const eligible = props.data.daily.filter(row => row[milestone.value].status === 'ready')
  return eligible.length ? t('visualGame.dashboard.retention.cohortRange', { since: eligible[0].date, until: eligible.at(-1).date }) : ''
})
function rate(value) {
  return new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2, style: 'percent' }).format(value / 100)
}
</script>

<template>
  <Section :title="t(`visualGame.dashboard.retention.${kind}`)" :data-testid="`dashboard-retention-${kind}`">
    <div class="retention__periods" role="group" :aria-label="t('visualGame.dashboard.retention.milestone')">
      <Button v-for="key in offsets" :key="key" size="sm" :variant="milestone === key ? 'primary' : 'secondary'" :aria-pressed="milestone === key" :data-testid="`retention-${kind}-${key}`" @click="milestone = key">{{ t(`visualGame.dashboard.retention.${key}`) }}</Button>
    </div>
    <div v-if="summary.rate !== null" class="retention__summary">
      <strong class="retention__value">{{ rate(summary.rate) }}</strong>
      <span>{{ t('visualGame.dashboard.retention.fraction', { retained: summary.retained_users, total: summary.cohort_users }) }}</span>
      <small>{{ eligible_range }}</small>
    </div>
    <div v-else class="retention__summary">
      <strong>{{ t(`visualGame.dashboard.retention.${pending ? 'pending' : 'empty'}`) }}</strong>
      <small>{{ t(`visualGame.dashboard.retention.${pending ? 'waitingNote' : 'emptyNote'}`) }}</small>
    </div>
    <p class="retention__note">{{ t('visualGame.dashboard.retention.chartNote') }}</p>
    <DashboardTrend v-if="has_data" :dates="data.daily.map(row => row.date)" :series="series" percentage interactive @select="inspect($event.name)" />
    <div v-else class="retention__empty" role="status">{{ t('visualGame.dashboard.retention.noTrend') }}</div>
    <p class="retention__note">{{ t('visualGame.dashboard.retention.chartSummary') }}</p>
    <Button size="sm" variant="secondary" :data-testid="`retention-${kind}-users`" @click="inspect(data.daily.filter(row => row.cohort_users > 0).at(-1)?.date || data.daily.at(-1)?.date)">{{ t('visualGame.dashboard.retention.drill.open') }}</Button>
    <p class="retention__note">{{ t('visualGame.dashboard.retention.drill.hint') }}</p>
    <RetentionUsersModal :target="target" :kind="kind" :dates="data.daily.map(row => row.date)" @close="target = null" />
    <details class="retention__rules">
      <summary>{{ t('visualGame.dashboard.retention.rules') }}</summary>
      <p class="retention__note">{{ t(`visualGame.dashboard.retention.${kind}Note`) }}</p>
      <p class="retention__note">{{ t('visualGame.dashboard.retention.methodology') }}</p>
    </details>
  </Section>
</template>

<style scoped>
.retention__periods { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); }
.retention__summary { display: flex; flex-wrap: wrap; align-items: baseline; gap: var(--drama-space-sm); margin: var(--drama-space-lg) 0; font-variant-numeric: tabular-nums; }
.retention__value { font-size: var(--drama-font-size-xl); }
.retention__summary small { flex-basis: 100%; }
.retention__note, .retention__summary span, small { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.retention__empty { display: grid; place-items: center; height: 320px; padding: var(--drama-space-lg); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); color: var(--drama-color-text-muted); text-align: center; }
.retention__rules { border-top: 1px solid var(--drama-color-border); padding-top: var(--drama-space-md); margin-top: var(--drama-space-md); }
summary { cursor: pointer; color: var(--drama-color-primary); }
summary:focus-visible { outline: 2px solid var(--drama-color-primary); }
</style>
